import { differenceInCalendarDays, format, parse, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { utils as xlsxUtils, write as writeXlsx } from 'xlsx';

import type {
  YardStepEventStatus,
  YardGateAppointmentDto,
  YardGateAppointmentFlow,
  YardGateAppointmentStatus,
  YardGateAppointmentType,
  YardGateRegistrationType,
  YardGateTrackedStep,
  YardGateTrackedStepColumn,
  YardGateDateRange,
  YardGateArea,
  YardGateDock,
  YardGateFieldAudit,
  YardGateOption,
} from 'contracts/YardGate';

export interface YardGateWarehouseOption extends YardGateOption {
  document: string;
}

export interface YardGateEntry {
  id: number | string;
  orderId: number | null;
  type: YardGateAppointmentType;
  registrationType?: YardGateRegistrationType;
  sourceId: number;
  status: YardGateAppointmentStatus;
  date: string;
  scheduledAt: string;
  startTime: string | null;
  endTime: string | null;
  entryAuthorizedAt: string | null;
  entryAuthorizedBy: { id: number; name: string } | null;
  entryAuthorizedByName: string;
  exitAuthorizedAt: string | null;
  exitAuthorizedBy: { id: number; name: string } | null;
  checklistAt: string | null;
  gateArrivedAt: string | null;
  overhaulAt: string | null;
  overhaulReleaseAt: string | null;
  driverCpf: string;
  driverName: string;
  phone: string;
  observation: string;
  annotation: string;
  area: YardGateArea[];
  docks: YardGateDock[];
  dockAttentionRequired?: boolean;
  dockAttentionCount: number;
  phoneAudit: YardGateFieldAudit;
  docksAudit: YardGateFieldAudit;
  observationAudit: YardGateFieldAudit;
  cancellationReason: { id: number; name: string } | null;
  customerId: number | null;
  customerName: string;
  carrierId: number | null;
  carrierName: string;
  vehicleTypeId: number | null;
  warehouseId: number | null;
  warehouseName: string;
  plate: string;
  trailerPlate: string | null;
  dock: string | null;
  orderTypeId: number | null;
  orderType: string | null;
  orderNumber: string;
  invoiceNumber: string | null;
  flow: YardGateAppointmentFlow | null;
  hasPortariaStep: boolean;
}

export interface YardGateLocalFilters {
  appointmentType: YardGateRegistrationType | '';
  statuses: YardGateAppointmentStatus[];
  driverCpf: string;
  driverName: string;
  phone: string;
  orderNumber: string;
  orderTypeId: number | '';
  carrierId: number | '';
  customerId: number | '';
  warehouseIds: number[];
  plate: string;
  dock: string;
}

export interface YardGateFilters
  extends YardGateDateRange, YardGateLocalFilters {}

export const DEFAULT_YARD_GATE_LOCAL_FILTERS: YardGateLocalFilters = {
  appointmentType: '',
  statuses: ['PENDING'],
  driverCpf: '',
  driverName: '',
  phone: '',
  orderNumber: '',
  orderTypeId: '',
  carrierId: '',
  customerId: '',
  warehouseIds: [],
  plate: '',
  dock: '',
};

export const getDefaultYardGateFilters = (
  now: Date = new Date()
): YardGateFilters => {
  const today = formatInTimeZone(now, 'America/Sao_Paulo', 'yyyy-MM-dd');
  return {
    ...DEFAULT_YARD_GATE_LOCAL_FILTERS,
    dateFrom: today,
    dateTo: today,
  };
};

const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

const normalizeCode = (value: string): string =>
  normalizeText(value).replace(/[^A-Z0-9]/g, '');

const formatCpf = (value: string): string => {
  if (value === '-') return value;
  const cpf = value.replace(/\D/g, '');
  return cpf.length === 11
    ? `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`
    : value;
};

export const yardGatePhoneDigits = (value: string): string =>
  value.replace(/\D/g, '');

export const formatYardGatePhone = (value: string): string => {
  const digits = yardGatePhoneDigits(value);
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return value;
};

const optionalValue = (value: string): string | null =>
  value === '-' ? null : value;

export const filterDisplayTrackedStepColumns = (
  columns: YardGateTrackedStepColumn[]
): YardGateTrackedStepColumn[] =>
  columns.filter(
    ({ type }) =>
      type === 'CHECKLIST' || type === 'ECLUSA' || type === 'PATIO_EXTERNO'
  );

const EMPTY_FIELD_AUDIT: YardGateFieldAudit = {
  updatedAt: null,
  updatedBy: null,
};

export const mapYardGateAppointment = (
  dto: YardGateAppointmentDto
): YardGateEntry => ({
  id: dto.id,
  orderId: dto.orderId,
  type: dto.type,
  registrationType: dto.registrationType ?? dto.type,
  sourceId: dto.sourceId,
  status: dto.status,
  date: format(parse(dto.date, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd'),
  scheduledAt: dto.scheduledAt,
  startTime: optionalValue(dto.entryTime),
  endTime: optionalValue(dto.exitTime),
  entryAuthorizedAt: dto.entryAuthorizedAt,
  entryAuthorizedBy: dto.entryAuthorizedBy,
  entryAuthorizedByName:
    dto.entryAuthorizedByName === '-' ? '' : dto.entryAuthorizedByName,
  exitAuthorizedAt: dto.exitAuthorizedAt,
  exitAuthorizedBy: dto.exitAuthorizedBy,
  checklistAt: optionalValue(dto.checklistAt),
  gateArrivedAt: optionalValue(dto.gateArrivedAt),
  overhaulAt: optionalValue(dto.overhaulChecklistAt),
  overhaulReleaseAt: optionalValue(dto.overhaulReleasedAt),
  driverCpf: formatCpf(dto.driverCpf),
  driverName: dto.driverName,
  phone: dto.phone === '-' ? '' : dto.phone,
  observation: dto.observation === '-' ? '' : dto.observation,
  annotation: dto.annotation === '-' ? '' : dto.annotation,
  area: dto.area ?? [],
  docks: dto.docks ?? [],
  dockAttentionRequired: dto.dockAttentionRequired ?? false,
  dockAttentionCount: dto.dockAttentionCount ?? 0,
  phoneAudit: dto.phoneAudit ?? EMPTY_FIELD_AUDIT,
  docksAudit: dto.docksAudit ?? EMPTY_FIELD_AUDIT,
  observationAudit: dto.observationAudit ?? EMPTY_FIELD_AUDIT,
  cancellationReason: dto.cancellationReason,
  customerId: dto.companyId,
  customerName: dto.customerName,
  carrierId: dto.carrierId,
  carrierName: dto.carrierName,
  vehicleTypeId: dto.vehicleTypeId,
  warehouseId: dto.warehouseId,
  warehouseName: dto.warehouseName,
  plate: normalizeCode(dto.plate),
  trailerPlate: optionalValue(dto.trailerPlate),
  dock: optionalValue(dto.dock),
  orderTypeId: dto.orderTypeId,
  orderType: optionalValue(dto.orderType),
  orderNumber: dto.collectionOrder,
  invoiceNumber: optionalValue(dto.invoiceNumber),
  flow: dto.flow,
  hasPortariaStep: dto.hasPortariaStep ?? true,
});

export const yardGateSnapshotFingerprint = (
  entries: YardGateEntry[],
  trackedStepColumns: YardGateTrackedStepColumn[]
): string => JSON.stringify({ entries, trackedStepColumns });

export type YardGateDateRangeErrors = Partial<
  Record<'dateFrom' | 'dateTo', string>
>;

export const validateYardGateDateRange = ({
  dateFrom,
  dateTo,
}: YardGateDateRange): YardGateDateRangeErrors => {
  if (!dateFrom && !dateTo) {
    return { dateFrom: 'Informe ao menos uma data.' };
  }
  if (!dateFrom || !dateTo) return {};

  const start = parseISO(dateFrom);
  const end = parseISO(dateTo);
  if (differenceInCalendarDays(end, start) < 0) {
    return {
      dateTo: 'A data final deve ser igual ou posterior à inicial.',
    };
  }
  if (differenceInCalendarDays(end, start) + 1 > 31) {
    return { dateTo: 'O intervalo máximo é de 31 dias.' };
  }
  return {};
};

const includesText = (candidate: string, filter: string): boolean =>
  !filter || normalizeText(candidate).includes(normalizeText(filter));

const matchesId = (candidate: number | null, filter: number | ''): boolean =>
  filter === '' || candidate === Number(filter);

export const yardGateDockNames = (entry: YardGateEntry): string[] =>
  entry.docks.map(({ name }) => name);

export const yardGateDockLabel = (entry: YardGateEntry): string =>
  yardGateDockNames(entry).join(', ');

const matchesYardGateFilters = (
  entry: YardGateEntry,
  filters: YardGateLocalFilters
): boolean =>
  (!filters.appointmentType ||
    (entry.registrationType ?? entry.type) === filters.appointmentType) &&
  (!filters.statuses.length || filters.statuses.includes(entry.status)) &&
  (!filters.driverCpf ||
    normalizeCode(entry.driverCpf).includes(
      normalizeCode(filters.driverCpf)
    )) &&
  includesText(entry.driverName, filters.driverName) &&
  (!filters.phone ||
    normalizeCode(entry.phone).includes(normalizeCode(filters.phone))) &&
  includesText(entry.orderNumber, filters.orderNumber) &&
  matchesId(entry.orderTypeId, filters.orderTypeId) &&
  matchesId(entry.carrierId, filters.carrierId) &&
  matchesId(entry.customerId, filters.customerId) &&
  (!filters.warehouseIds.length ||
    (entry.warehouseId !== null &&
      filters.warehouseIds.includes(entry.warehouseId))) &&
  (!filters.plate ||
    normalizeCode(entry.plate).includes(normalizeCode(filters.plate))) &&
  includesText(yardGateDockLabel(entry), filters.dock);

export const filterYardGateEntries = (
  entries: YardGateEntry[],
  filters: YardGateLocalFilters
): YardGateEntry[] =>
  entries.filter((entry) => matchesYardGateFilters(entry, filters));

export const countAppliedYardGateFilters = (
  filters: YardGateLocalFilters
): number =>
  Number(filters.appointmentType !== '') +
  Number(filters.statuses.length > 0) +
  Number(filters.warehouseIds.length > 0) +
  [
    filters.driverCpf,
    filters.driverName,
    filters.phone,
    filters.orderNumber,
    filters.orderTypeId,
    filters.carrierId,
    filters.customerId,
    filters.plate,
    filters.dock,
  ].filter((value) => value !== '').length;

export const STEP_EVENT_STATUS_LABEL: Record<YardStepEventStatus, string> = {
  PENDING: 'Pendente',
  IN_PROGRESS: 'Em andamento',
  COMPLETED: 'Concluído',
  SKIPPED: 'Ignorado',
  CANCELLED: 'Cancelado',
  REJECTED: 'Rejeitado',
};

export const formatTrackedStepTime = (
  value: string | null,
  pattern = 'HH:mm'
): string => (value ? format(parseISO(value), pattern) : '—');

export const GATE_ARRIVAL_STEP_TYPE = 'PORTARIA';

export const formatScheduledAt = (entry: YardGateEntry): string =>
  entry.scheduledAt
    ? format(parseISO(entry.scheduledAt), 'dd/MM/yyyy HH:mm')
    : entry.date;

export const formatFieldAudit = (audit: YardGateFieldAudit): string =>
  audit.updatedAt
    ? `${audit.updatedBy?.name ?? ''} em ${format(
        parseISO(audit.updatedAt),
        'dd/MM/yyyy HH:mm'
      )}`
    : '';

export const gateArrivalTrackedStep = (
  entry: YardGateEntry
): YardGateTrackedStep | null =>
  entry.flow?.trackedSteps.find(
    ({ type }) => type === GATE_ARRIVAL_STEP_TYPE
  ) ?? null;

export const MISSING_STEP_LABEL = 'N/A';

export const formatGateArrivalStart = (entry: YardGateEntry): string => {
  if (entry.type === 'STANDALONE') {
    return formatTrackedStepTime(entry.gateArrivedAt, 'dd/MM/yy HH:mm');
  }
  const step = gateArrivalTrackedStep(entry);
  return step
    ? formatTrackedStepTime(step.startedAt, 'dd/MM/yy HH:mm')
    : MISSING_STEP_LABEL;
};

export const formatTrackedStepValue = (
  step: YardGateTrackedStep | null
): string => {
  if (!step) return '';
  const parts = [STEP_EVENT_STATUS_LABEL[step.status]];
  if (step.startedAt) {
    parts.push(
      `${format(parseISO(step.startedAt), 'HH:mm')} (${
        step.startedBy?.name ?? ''
      })`
    );
  }
  if (step.finishedAt) {
    parts.push(
      `${format(parseISO(step.finishedAt), 'HH:mm')} (${
        step.finishedBy?.name ?? ''
      })`
    );
  }
  return parts.join(' | ');
};

export const YARD_GATE_STATUS_LABEL: Record<YardGateAppointmentStatus, string> =
  {
    PENDING: 'Pendente',
    AWAITING_RELEASE: 'Ag. Liberação',
    ENTRY_AUTHORIZED: 'Lib. Entrada',
    EXIT_AUTHORIZED: 'Lib. Saída',
    CANCELLED: 'Cancelada',
    NO_SHOW: 'No Show',
  };

const trackedStepValue = (entry: YardGateEntry, code: string): string =>
  formatTrackedStepValue(
    entry.flow?.trackedSteps.find((trackedStep) => trackedStep.code === code) ??
      null
  );

export const gateArrivalValue = (entry: YardGateEntry): string => {
  const value = formatGateArrivalStart(entry);
  return value === MISSING_STEP_LABEL || value === '—' ? '' : value;
};

type YardGateExportCell = string | number | null;

const exportText = (value: YardGateExportCell): YardGateExportCell => {
  if (value === null || value === undefined) return '';
  if (value === '-' || value === '—' || value === 'N/A') return '';
  return value;
};

const yardGateExportRow = (
  entry: YardGateEntry,
  trackedStepColumns: YardGateTrackedStepColumn[]
): YardGateExportCell[] => [
  YARD_GATE_STATUS_LABEL[entry.status],
  entry.registrationType === 'HORA_CERTA'
    ? 'HORA CERTA'
    : entry.type === 'ORDER'
      ? 'ORDEM'
      : 'AVULSO',
  formatScheduledAt(entry),
  entry.driverCpf,
  entry.driverName,
  entry.entryAuthorizedByName,
  formatYardGatePhone(entry.phone),
  formatFieldAudit(entry.phoneAudit),
  gateArrivalValue(entry),
  entry.dock,
  yardGateDockLabel(entry),
  formatFieldAudit(entry.docksAudit),
  entry.area.map(({ name }) => name).join(', '),
  entry.startTime,
  entry.endTime,
  entry.plate,
  entry.trailerPlate,
  entry.carrierName,
  entry.customerName,
  entry.warehouseName,
  entry.invoiceNumber,
  entry.orderNumber,
  entry.orderType,
  entry.cancellationReason?.name ?? null,
  ...trackedStepColumns.map(({ code }) => trackedStepValue(entry, code)),
  entry.observation,
  entry.annotation,
  formatFieldAudit(entry.observationAudit),
];

const csvCell = (value: string | number | null): string =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

const yardGateExportHeaders = (
  trackedStepColumns: YardGateTrackedStepColumn[]
): YardGateExportCell[] => [
  'Status',
  'Tipo',
  'Data Agendamento',
  'CPF',
  'Motorista',
  'Autorizado por',
  'Telefone',
  'Telefone alterado por',
  'P. Chegada',
  'Doca Agendamento',
  'Doca física',
  'Doca alterado por',
  'Área',
  'H. Entrada',
  'H. Saída',
  'Placa',
  'Carreta',
  'Transp./Prest. Serviço',
  'Cliente',
  'Armazém',
  'NF',
  'Ordem',
  'Tipo de ordem',
  'Motivo do cancelamento',
  ...trackedStepColumns.map(({ name }) => name),
  'Observação',
  'Anotação',
  'Anotação alterado por',
];

const buildYardGateExportMatrix = (
  entries: YardGateEntry[],
  trackedStepColumns: YardGateTrackedStepColumn[]
): YardGateExportCell[][] => [
  yardGateExportHeaders(trackedStepColumns),
  ...entries.map((entry) =>
    yardGateExportRow(entry, trackedStepColumns).map(exportText)
  ),
];

export const exportYardGateEntries = (
  entries: YardGateEntry[],
  trackedStepColumns: YardGateTrackedStepColumn[]
): Blob => {
  const content = buildYardGateExportMatrix(entries, trackedStepColumns)
    .map((row) => row.map(csvCell).join(';'))
    .join('\n');
  return new Blob([`\uFEFF${content}`], {
    type: 'text/csv;charset=utf-8',
  });
};

export const exportYardGateEntriesXlsx = (
  entries: YardGateEntry[],
  trackedStepColumns: YardGateTrackedStepColumn[]
): Blob => {
  const matrix = buildYardGateExportMatrix(entries, trackedStepColumns).map(
    (row) => row.map((value) => value ?? '')
  );
  const worksheet = xlsxUtils.aoa_to_sheet(matrix);
  const workbook = xlsxUtils.book_new();
  xlsxUtils.book_append_sheet(workbook, worksheet, 'Entradas');
  const content = writeXlsx(workbook, { bookType: 'xlsx', type: 'array' });

  return new Blob([content], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};
